import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  PaginationOptions,
  SavedViewsQuery,
  SavedViewsRequest,
} from "../../../generated/app-service-gql/graphql";
import { SavedViewDataType } from "../../../types/graphQLEnums";
import {
  SavedDocumentView,
  savedDocumentView,
  SavedNoticeSemanticView,
  savedNoticeSemanticView,
  SavedNoticeView,
  savedNoticeView,
  savedViewUnion,
} from "../../../types/savedViews";
import { useGraphQlQuery } from "../useGraphQlClient";

export type SavedViewResults<DataType extends SavedViewDataType> = DataType extends "notice"
  ? SavedNoticeView[]
  : DataType extends "document"
    ? SavedDocumentView[]
    : DataType extends "noticeSemantic"
      ? SavedNoticeSemanticView[]
      : never[];

const searchSavedViewsQuery = graphql(`
  query savedViews($request: SavedViewsRequest!, $pagination: PaginationOptions!) {
    savedViews(SavedViewsRequest: $request, PaginationOptions: $pagination) {
      results {
        id
        dataType
        name
        description
        view
        pinnedRank
        isStandardView
        owner {
          guid
          firstName
          lastName
          active
        }
        createdAt
        updatedAt
      }
      totalResults
    }
  }
`);

export function useSavedViews(
  searchSavedViewsRequest: SavedViewsRequest,
  pagination: PaginationOptions,
  options?: UseQueryOptions<SavedViewsQuery, unknown, SavedViewsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["savedViews", searchSavedViewsRequest, pagination],
    searchSavedViewsQuery,
    [{ request: searchSavedViewsRequest, pagination }],
    options,
  );

  const parsedViews = savedViewUnion.array().parse(data?.savedViews.results ?? []);
  const combinedData = {
    ...data?.savedViews,
    results: parsedViews,
  };

  return { data: combinedData, ...rest };
}

export function useSavedViewsDataType<DataType extends SavedViewDataType>(
  dataType: DataType,
  searchSavedViewsRequest: Omit<SavedViewsRequest, "dataTypes">,
  pagination: PaginationOptions,
  options?: UseQueryOptions<SavedViewsQuery, unknown, SavedViewsQuery, QueryKey>,
): {
  data:
    | {
        results: SavedViewResults<DataType>;
        totalResults?: number;
      }
    | undefined;
} & Omit<ReturnType<typeof useSavedViews>, "data"> {
  const { data, ...rest } = useGraphQlQuery(
    ["savedViews", dataType, searchSavedViewsRequest, pagination],
    searchSavedViewsQuery,
    [
      {
        request: {
          ...searchSavedViewsRequest,
          dataTypes: [dataType],
        },
        pagination,
      },
    ],
    options,
  );

  let typedResults: SavedViewResults<DataType>;

  if (dataType === "notice") {
    const noticeResults = savedNoticeView.array().parse(data?.savedViews?.results ?? []);
    typedResults = noticeResults as SavedViewResults<DataType>;
  } else if (dataType === "document") {
    const documentResults = savedDocumentView.array().parse(data?.savedViews?.results ?? []);
    typedResults = documentResults as SavedViewResults<DataType>;
  } else if (dataType === "noticeSemantic") {
    const noticeSemanticResults = savedNoticeSemanticView
      .array()
      .parse(data?.savedViews?.results ?? []);
    typedResults = noticeSemanticResults as SavedViewResults<DataType>;
  } else {
    throw new Error(`Unsupported dataType for savedViews: ${dataType}`);
  }

  const combinedData = {
    ...data?.savedViews,
    results: typedResults,
  };

  return { data: combinedData, ...rest };
}
